/**
 * Property Stewardship Document
 * Phase 2 document for real estate contributions
 * No signatures required - informational document
 */

import { replaceMergeFields, getAllMergeFields } from './mergeFields';

export function generatePropertyStewardshipDocHtml(
  workflowData: any,
  unaData: any
): string {
  const mergeFields = getAllMergeFields(workflowData, unaData);
  
  const template = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body {
      font-family: 'Cambria', 'Georgia', serif;
      font-size: 12pt;
      line-height: 1.6;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 1in;
    }
    .header {
      text-align: center;
      border-bottom: 2px solid #333;
      padding-bottom: 0.3in;
      margin-bottom: 0.4in;
    }
    h1 {
      font-size: 16pt;
      margin: 0 0 0.1in 0;
      font-weight: bold;
    }
    .subtitle {
      font-size: 10pt;
      font-style: italic;
      color: #666;
    }
    .section {
      margin: 0.3in 0;
    }
    .section-title {
      font-weight: bold;
      font-size: 11pt;
      margin-bottom: 0.15in;
      text-transform: uppercase;
      border-bottom: 1px solid #333;
      padding-bottom: 0.05in;
    }
    p {
      margin: 0.15in 0;
      text-align: justify;
    }
    .info-box {
      border: 1px solid #333;
      padding: 0.2in;
      margin: 0.2in 0;
      background-color: #f9f9f9;
    }
    .info-label {
      font-weight: bold;
      display: inline-block;
      min-width: 2in;
    }
    .checklist {
      margin: 0.2in 0;
    }
    .checklist-item {
      margin: 0.1in 0 0.1in 0.3in;
    }
    .checkbox {
      display: inline-block;
      width: 0.12in;
      height: 0.12in;
      border: 1px solid #333;
      margin-right: 0.1in;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Property Stewardship Guidelines</h1>
    <div class="subtitle">For Private Ecclesiastical Real Property</div>
  </div>
  
  <div class="info-box">
    <div><span class="info-label">Property Address:</span> {{property_address}}, {{property_city}}, {{property_state}} {{property_zip}}</div>
    <div><span class="info-label">County:</span> {{property_county}}</div>
    <div><span class="info-label">Steward Entity:</span> {{receiving_entity}}</div>
    <div><span class="info-label">Ecclesiastical Association:</span> {{una_name}}</div>
    <div><span class="info-label">Contribution Date:</span> {{contribution_date}}</div>
  </div>
  
  <div class="section">
    <div class="section-title">Purpose of This Document</div>
    <p>
      This Property Stewardship Document establishes the operational framework and 
      responsibilities for the ongoing stewardship of the real property contributed to 
      {{una_name}} through the private ecclesiastical contribution process. This property 
      is now held in trust for the benefit of the membership and in accordance with the 
      sacred purposes outlined in the association's governing documents.
    </p>
  </div>
  
  <div class="section">
    <div class="section-title">Stewardship Principles</div>
    <p>
      The stewardship of this property shall be guided by the following principles:
    </p>
    <p>
      <strong>Sacred Trust:</strong> The property is held in sacred trust for the benefit 
      of current and future members of {{una_name}}, and shall be managed with the highest 
      degree of care and diligence.
    </p>
    <p>
      <strong>Private Ecclesiastical Status:</strong> The property retains its character 
      as private ecclesiastical property, outside the jurisdiction of public law commercial 
      property regulations.
    </p>
    <p>
      <strong>Mission Alignment:</strong> All uses of the property shall align with the 
      mission, values, and purposes of {{una_name}} as established in its governing documents.
    </p>
    <p>
      <strong>Sustainability:</strong> Property management shall prioritize long-term 
      sustainability and preservation for future generations of members.
    </p>
  </div>
  
  <div class="section">
    <div class="section-title">Initial Stewardship Tasks</div>
    <div class="checklist">
      <div class="checklist-item">
        <span class="checkbox"></span> Update property insurance policies to reflect new stewardship
      </div>
      <div class="checklist-item">
        <span class="checkbox"></span> Notify utility companies of ecclesiastical status
      </div>
      <div class="checklist-item">
        <span class="checkbox"></span> Document current property condition with photographs
      </div>
      <div class="checklist-item">
        <span class="checkbox"></span> Create property maintenance schedule
      </div>
      <div class="checklist-item">
        <span class="checkbox"></span> Establish property access protocols for members
      </div>
      <div class="checklist-item">
        <span class="checkbox"></span> File ecclesiastical declaration with county recorder (if applicable)
      </div>
      <div class="checklist-item">
        <span class="checkbox"></span> Update {{una_name}} asset inventory records
      </div>
    </div>
  </div>
  
  <div class="section">
    <div class="section-title">Ongoing Stewardship Responsibilities</div>
    <p>
      <strong>Regular Maintenance:</strong> Ensure the property is maintained in good 
      condition through regular inspections, preventive maintenance, and timely repairs.
    </p>
    <p>
      <strong>Financial Stewardship:</strong> Manage all property-related expenses through 
      the association's financial systems, with appropriate documentation and oversight.
    </p>
    <p>
      <strong>Member Access:</strong> Facilitate appropriate member access to and use of 
      the property in accordance with association policies.
    </p>
    <p>
      <strong>Record Keeping:</strong> Maintain comprehensive records of all property-related 
      activities, maintenance, improvements, and expenses.
    </p>
    <p>
      <strong>Compliance Monitoring:</strong> Monitor ongoing compliance with all applicable 
      private ecclesiastical property standards and association policies.
    </p>
  </div>
  
  <div class="section">
    <div class="section-title">Authority and Oversight</div>
    <p>
      Day-to-day stewardship of this property is delegated to {{receiving_entity}}, 
      under the oversight of the {{primary_officer_role}} of {{una_name}}. Major decisions 
      regarding the property, including significant improvements, changes in use, or 
      disposition, shall require approval from the appropriate governing body as specified
      in the association's operating documents.
    </p>
  </div>
  
  <div style="margin-top: 0.5in; padding-top: 0.3in; border-top: 1px solid #666;">
    <div style="text-align: center; font-size: 10pt; color: #666;">
      Property Stewardship Document prepared {{current_date}}<br>
      For ecclesiastical property management purposes<br>
      {{una_name}}
    </div>
  </div>
</body>
</html>
  `;
  
  return replaceMergeFields(template, mergeFields);
}
